import { Box, Flex, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react'
import React from 'react'
import { RiStackLine } from 'react-icons/ri'
import type { Abi, AbiFunction } from 'viem'
import { useVerifiedContractQuery } from 'apollo/generated/graphqlClient'
import { ReadContract } from 'components/AddressDetailTabs/tabs/ReadContract'
import { WriteContract } from 'components/AddressDetailTabs/tabs/WriteContract'
import { ExternalLink } from 'components/ExternalLink'
import { Link } from 'components/Link'
import { Routes } from 'constants/routes'
import { useLocationHash } from 'hooks/useLocationHash'
import { useThemeColors } from 'hooks/useThemeColors'
import { getIsContractMethodRead, getIsContractMethodWrite } from 'utils/common'

type DelegationTitleProps = {
  delegationAddress: string
}

const DelegationTitle = ({ delegationAddress }: DelegationTitleProps) => (
  <Flex alignItems="center" mb={4}>
    <RiStackLine />
    <Text ml={1.5} variant="text2medium">
      ABI for the delegation contract at
    </Text>
    <Link
      ml={1}
      href={Routes.addressDetail({
        hash: delegationAddress,
        activeTab: 3,
      })}
    >
      {delegationAddress}
    </Link>
    <Text variant="text2medium">, using the</Text>
    <ExternalLink mx={1} href="https://eips.ethereum.org/EIPS/eip-7702" label="EIP-7702" />
    <Text variant="text2medium">pattern.</Text>
  </Flex>
)
type EIP7702DelegationProps = {
  delegationAddress: string
  bytecode: string
}

export const EIP7702Delegation = ({ delegationAddress, bytecode }: EIP7702DelegationProps) => {
  useLocationHash()

  const COLORS = useThemeColors()

  const { data: delegationContractData, loading: isDelegationContractLoading } =
    useVerifiedContractQuery({
      variables: { hash: delegationAddress },
      skip: !delegationAddress.length,
    })

  return (
    <Tabs>
      <TabList as={Flex} borderRadius={0}>
        <Tab>Code</Tab>
        <Tab>Read</Tab>
        <Tab>Write</Tab>
      </TabList>
      <TabPanels>
        <TabPanel p={5}>
          <Flex alignItems="center" mb={4}>
            <RiStackLine />
            <Text ml={1.5} variant="text2medium">
              Delegating to the contract at
            </Text>
            <Link
              ml={1}
              href={Routes.addressDetail({
                hash: delegationAddress,
                activeTab: 3,
              })}
            >
              {delegationAddress}
            </Link>
            <Text variant="text2medium">, using the</Text>
            <ExternalLink
              mx={1}
              href="https://eips.ethereum.org/EIPS/eip-7702"
              label="EIP-7702 Transparent Proxy"
            />
            <Text variant="text2medium">pattern.</Text>
          </Flex>
          <Box
            mt={4}
            p={5}
            bgColor={COLORS.white}
            border="1px solid"
            maxH={200}
            overflow="auto"
            borderRadius={6}
            borderColor={COLORS.grey06}
          >
            <Text variant="text3mono" color={COLORS.grey01}>
              {bytecode}
            </Text>
          </Box>
        </TabPanel>
        <TabPanel p={5}>
          {isDelegationContractLoading && !delegationContractData && <Spinner />}
          {!isDelegationContractLoading && delegationContractData && (
            <ReadContract
              data={
                (JSON.parse(delegationContractData.verifiedContract?.abi ?? '[]') as Abi).filter(
                  (abiItem) =>
                    'stateMutability' in abiItem &&
                    getIsContractMethodRead(abiItem.stateMutability ?? '', abiItem.type)
                ) as AbiFunction[]
              }
              hash={delegationAddress}
              title={<DelegationTitle delegationAddress={delegationAddress} />}
            />
          )}
        </TabPanel>
        <TabPanel p={5}>
          {isDelegationContractLoading && !delegationContractData && <Spinner />}
          {!isDelegationContractLoading && delegationContractData && (
            <WriteContract
              data={
                (JSON.parse(delegationContractData.verifiedContract?.abi ?? '[]') as Abi).filter(
                  (abiItem) =>
                    'stateMutability' in abiItem &&
                    getIsContractMethodWrite(abiItem.stateMutability ?? '', abiItem.type)
                ) as AbiFunction[]
              }
              hash={delegationAddress}
              title={<DelegationTitle delegationAddress={delegationAddress} />}
            />
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
}
